import React, { useEffect, useState, useMemo } from 'react';
import { Card, Table, Tag, Typography, Radio, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createAuthApi } from '@zero/auth-admin-ui';
import { useGridPageSize, GRID_PAGE_SIZE_OPTIONS } from '../../hooks/useGridPageSize.js';

const { Text } = Typography;

export function PermissionsPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moduleFilter, setModuleFilter] = useState('__all__');
  const [text, setText] = useState('');
  const [pageSize, setPageSize] = useGridPageSize('permissions', 25);

  useEffect(() => {
    setLoading(true);
    api.listPermissions().then((r) => setPerms(r.data?.Data ?? [])).finally(() => setLoading(false));
  }, [api]);

  // Danh sách module (cho radio filter) — giữ thứ tự xuất hiện
  const modules = useMemo(() => {
    const seen = [];
    for (const p of perms) if (p.Module && !seen.includes(p.Module)) seen.push(p.Module);
    return seen;
  }, [perms]);

  // Tên quyền (dịch DisplayKey; fallback Action) — dùng cho hiển thị + tìm kiếm
  const nameOf = (p) => t(p.DisplayKey, { defaultValue: p.Action || p.Code });
  // Mô tả chi tiết (permDesc.<code lowercase>); chưa có -> để trống.
  const descOf = (p) => t('permDesc.' + (p.Code || '').toLowerCase(), { defaultValue: '' });

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    return perms.filter((p) => {
      if (moduleFilter !== '__all__' && p.Module !== moduleFilter) return false;
      if (q && !(`${p.Code} ${nameOf(p)}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [perms, moduleFilter, text, t]);

  const columns = [
    { title: t('permission.module', 'Module'), dataIndex: 'Module', width: 110,
      render: (v) => <Tag>{v}</Tag> },
    { title: t('permission.name', 'Tên quyền'), key: 'name', width: 220, render: (_, p) => <strong>{nameOf(p)}</strong>,
      sorter: (a, b) => nameOf(a).localeCompare(nameOf(b)) },
    { title: t('permission.desc', 'Mô tả'), key: 'desc',
      render: (_, p) => <Text type="secondary">{descOf(p)}</Text> },
    { title: t('permission.code', 'Mã quyền'), dataIndex: 'Code', width: 260,
      render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
      sorter: (a, b) => a.Code.localeCompare(b.Code) },
    { title: t('common.status', 'Trạng thái'), dataIndex: 'IsObsolete', width: 110, align: 'center',
      render: (v) => v ? <Tag color="warning">Obsolete</Tag> : <Tag color="success">{t('common.active', 'Hoạt động')}</Tag> },
  ];

  return (
    <Card size="small" styles={{ body: { padding: 8 } }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <Radio.Group value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} optionType="button" buttonStyle="solid" size="small">
          <Radio.Button value="__all__">{t('common.all', 'Tất cả')}</Radio.Button>
          {modules.map((m) => <Radio.Button key={m} value={m}>{m}</Radio.Button>)}
        </Radio.Group>
        <Input allowClear prefix={<SearchOutlined />} style={{ width: 260 }}
               placeholder={t('permission.searchPh', 'Tìm theo tên / mã quyền')}
               value={text} onChange={(e) => setText(e.target.value)} />
        <Text type="secondary" style={{ fontSize: 12 }}>{filtered.length} / {perms.length}</Text>
      </div>
      <Table dataSource={filtered} columns={columns} loading={loading} rowKey="PermissionID" size="small" bordered
             pagination={{
               pageSize,
               showSizeChanger: true,
               pageSizeOptions: GRID_PAGE_SIZE_OPTIONS,
               onShowSizeChange: (_, size) => setPageSize(size),
             }}
             scroll={{ y: 'calc(100vh - 170px)' }} />
    </Card>
  );
}
